import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Modal, Form, Button } from "react-bootstrap";
import {
  BsBoxArrowInLeft,
  BsBoxArrowInRight,
  BsPersonCircle,
  BsEnvelope,
  BsLock,
  BsExclamationTriangle,
  BsArrowRightShort,
  BsEye,
  BsEyeSlash,
  BsHouseDoor,
  BsList,
  BsX,
} from "react-icons/bs";
import {
  signOutUser,
  initAuthStateListener,
  signInWithEmailPassword,
} from "../services/authService";
import { selectUser } from "../features/user/userSlice";

import { useI18n, LanguageSwitcher } from "../v2/i18n/I18nProvider";
import { useAdminAuth } from "../admin/hooks/useAdminAuth";
import "../css/auth-modal.css";

// ─── Design tokens ──────────────────────────────────────────────
const C = {
  bg: "#0a0a0a",
  bgScroll: "rgba(10,10,10,0.95)",
  gold: "#fdc029",
  goldHover: "#ffe07a",
  white: "#ffffff",
  muted: "rgba(255,255,255,0.55)",
  border: "rgba(255,255,255,0.07)",
  radius: 10,
  // 69 + 1px de borderBottom = 70 = exactamente el `padding-top` del body
  // (index.css) y el token --ra-nav-h. Si no coinciden queda una franja del
  // fondo del body destapada bajo la barra — y ese fondo lo pinta de BLANCO
  // el reboot de Bootstrap (se importa después de index.css en src/index.js).
  navHeight: 69,
  transition: "all .25s cubic-bezier(.4,0,.2,1)",
};

// ─── Component ──────────────────────────────────────────────────
const ResponsiveAppBar = () => {
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { isAdmin } = useAdminAuth();

  const [showModal, setShowModal] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hovered, setHovered] = useState(null);

  const mobileMenuRef = useRef(null);

  // ── Scroll listener ────────────────────────────────────────────
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ── Auth listener ──────────────────────────────────────────────
  useEffect(() => {
    const unsubscribe = initAuthStateListener(dispatch);
    return () => unsubscribe();
  }, [dispatch]);

  // ── Close mobile menu on route change ──────────────────────────
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // ── Lock body scroll when mobile menu is open ──────────────────
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  // ── Auth handlers ──────────────────────────────────────────────
  const handleSignOut = useCallback(async () => {
    try { await signOutUser(dispatch); }
    catch (err) { console.log(err); }
  }, [dispatch]);

  const handleSignIn = useCallback(async () => {
    if (!email || !password) {
      setError(t("navbar.auth.emptyFields"));
      return;
    }
    setLoading(true);
    setError("");
    try {
      const userCredential = await signInWithEmailPassword(email.trim(), password, dispatch);
      setShowModal(false);
      resetForm();
      if (userCredential?.user?.uid) {
        // Si llegaste queriendo entrar al admin (p.ej. abriste el ícono "RA Admin" del
        // iPhone, que arranca sin sesión), volvés ahí; si no, a tu perfil como siempre.
        let next = "";
        try { next = sessionStorage.getItem("ra_after_login") || ""; sessionStorage.removeItem("ra_after_login"); } catch { /* sin storage */ }
        navigate(/^\/admin(\/|$|\?)/.test(next) ? next : `/profile/${userCredential.user.uid}`);
      }
    } catch (err) {
      console.error("Sign-in error:", err);
      const map = {
        "auth/user-not-found": "navbar.auth.userNotFound",
        "auth/wrong-password": "navbar.auth.wrongPassword",
        "auth/invalid-email": "navbar.auth.invalidEmail",
        "auth/too-many-requests": "navbar.auth.tooManyRequests",
        "auth/invalid-credential": "navbar.auth.invalidCredential",
      };
      setError(t(map[err.code] || "navbar.auth.genericError"));
    } finally {
      setLoading(false);
    }
  }, [email, password, dispatch, t]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter") { e.preventDefault(); handleSignIn(); }
  };

  const resetForm = () => { setEmail(""); setPassword(""); setError(""); setLoading(false); };
  const closeModal = () => { setShowModal(false); resetForm(); };

  // ── Active path helper ─────────────────────────────────────────
  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  // ── Build nav items ────────────────────────────────────────────
  const navItems = [];
  navItems.push({ label: t("navbar.home"), to: user ? "/home" : "/", icon: <BsHouseDoor size={16} />, check: user ? "/home" : "/" });
  if (user) {
    navItems.push({
      label: t("navbar.myAccount"),
      to: `/profile/${user.uid}`,
      icon: <BsPersonCircle size={16} />,
      check: "/profile",
    });

    if (isAdmin) {
      navItems.push({
        label: "Admin",
        to: "/admin",
        icon: <BsPersonCircle size={16} />,
        check: "/admin",
      });
    }
  }

  // Safe-area en PWA instalada (standalone): el contenido va bajo el notch /
  // barra de estado del iPhone. Solo aplica como app instalada → no toca Safari.
  const isStandalone =
    typeof window !== "undefined" &&
    ((window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      window.navigator.standalone === true);

  // ── Inline styles ──────────────────────────────────────────────
  const s = {
    navbar: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      paddingTop: isStandalone ? "env(safe-area-inset-top)" : undefined,
      zIndex: 1050,
      background: scrolled ? C.bgScroll : C.bg,
      backdropFilter: scrolled ? "blur(14px)" : "none",
      WebkitBackdropFilter: scrolled ? "blur(14px)" : "none",
      borderBottom: `1px solid ${scrolled ? "rgba(255,255,255,0.04)" : C.border}`,
      boxShadow: scrolled ? "0 4px 30px rgba(0,0,0,0.25)" : "none",
      transition: C.transition,
    },
    container: {
      maxWidth: 1200,
      margin: "0 auto",
      padding: "0 20px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      height: C.navHeight,
    },
    brand: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      textDecoration: "none",
      flexShrink: 0,
    },
    logo: {
      height: 38,
      width: "auto",
      objectFit: "contain",
      transition: C.transition,
    },
    brandText: {
      color: C.gold,
      fontWeight: 700,
      fontSize: 18,
      letterSpacing: "-0.3px",
    },
    desktopNav: {
      display: "flex",
      alignItems: "center",
      gap: 4,
    },
    navLink: (active, hover) => ({
      display: "flex",
      alignItems: "center",
      gap: 7,
      padding: "8px 14px",
      borderRadius: 8,
      fontSize: 14,
      fontWeight: 500,
      textDecoration: "none",
      transition: C.transition,
      cursor: "pointer",
      whiteSpace: "nowrap",
      border: "none",
      background: active
        ? "rgba(253,192,41,0.1)"
        : hover
          ? "rgba(255,255,255,0.06)"
          : "none",
      color: active ? C.gold : hover ? C.goldHover : "rgba(255,255,255,0.75)",
    }),
    activeIndicator: {
      position: "absolute",
      bottom: -1,
      left: "50%",
      transform: "translateX(-50%)",
      width: 18,
      height: 2,
      borderRadius: 1,
      background: C.gold,
    },
    authBtn: (hover) => ({
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 18px",
      borderRadius: 8,
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
      transition: C.transition,
      border: hover ? `1.5px solid ${C.gold}` : "1.5px solid rgba(255,255,255,0.2)",
      background: hover ? C.gold : "transparent",
      color: hover ? C.bg : C.white,
      whiteSpace: "nowrap",
    }),
    divider: {
      width: 1,
      height: 24,
      background: "rgba(255,255,255,0.1)",
      margin: "0 8px",
      flexShrink: 0,
    },
    hamburger: {
      display: "none",
      alignItems: "center",
      justifyContent: "center",
      width: 40,
      height: 40,
      borderRadius: 10,
      border: "none",
      background: "rgba(255,255,255,0.08)",
      color: C.white,
      cursor: "pointer",
      transition: C.transition,
      flexShrink: 0,
    },
    overlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.5)",
      backdropFilter: "blur(4px)",
      WebkitBackdropFilter: "blur(4px)",
      zIndex: 1060,
      opacity: mobileOpen ? 1 : 0,
      pointerEvents: mobileOpen ? "auto" : "none",
      transition: "opacity .3s ease",
    },
    drawer: {
      position: "fixed",
      top: 0,
      right: 0,
      bottom: 0,
      paddingTop: isStandalone ? "env(safe-area-inset-top)" : undefined,
      width: "min(320px, 85vw)",
      background: "#161616",
      zIndex: 1070,
      transform: mobileOpen ? "translateX(0)" : "translateX(100%)",
      transition: "transform .3s cubic-bezier(.4,0,.2,1)",
      display: "flex",
      flexDirection: "column",
      boxShadow: mobileOpen ? "-8px 0 40px rgba(0,0,0,0.4)" : "none",
    },
    drawerHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "16px 20px",
      borderBottom: `1px solid ${C.border}`,
    },
    drawerClose: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 36,
      height: 36,
      borderRadius: 10,
      border: "none",
      background: "rgba(255,255,255,0.08)",
      color: C.white,
      cursor: "pointer",
      transition: C.transition,
    },
    drawerBody: {
      flex: 1,
      overflowY: "auto",
      padding: "12px 16px",
      display: "flex",
      flexDirection: "column",
      gap: 4,
    },
    drawerLink: (active) => ({
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "14px 16px",
      borderRadius: 12,
      fontSize: 15,
      fontWeight: 500,
      textDecoration: "none",
      transition: C.transition,
      color: active ? C.gold : "rgba(255,255,255,0.8)",
      background: active ? "rgba(253,192,41,0.1)" : "transparent",
      fontWeight: active ? 600 : 500,
    }),
    drawerFooter: {
      padding: "16px 20px",
      borderTop: `1px solid ${C.border}`,
      display: "flex",
      flexDirection: "column",
      gap: 10,
    },
    mobileAuthBtn: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      padding: "12px 16px",
      borderRadius: 12,
      fontSize: 15,
      fontWeight: 600,
      cursor: "pointer",
      transition: C.transition,
      border: "none",
      width: "100%",
    },
  };

  // ───────────────────────────────────────────────────────────────
  // RENDER
  // ───────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Navbar bar ───────────────────────────────────── */}
      <nav style={s.navbar}>
        <div style={s.container}>
          {/* Brand */}
          <Link to={user ? `/profile/${user.uid}` : "/"} style={s.brand}>
            <img src="/images/logoRA.png" alt="RestaurAdmin" style={s.logo} />
            <span style={s.brandText}>{t("navbar.brand")}</span>
          </Link>

          {/* Desktop nav */}
          <DesktopNav
            navItems={navItems}
            user={user}
            hovered={hovered}
            setHovered={setHovered}
            isActive={isActive}
            handleSignOut={handleSignOut}
            openSignIn={() => setShowModal(true)}
            s={s}
            t={t}
          />

          {/* Mobile hamburger */}
          <button
            style={s.hamburger}
            className="ra-mobile-only"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <BsList size={22} />
          </button>
        </div>
      </nav>

      {/* ── Mobile drawer ────────────────────────────────── */}
      <div style={s.overlay} onClick={() => setMobileOpen(false)} />
      <div style={s.drawer} ref={mobileMenuRef}>
        <div style={s.drawerHeader}>
          <Link to={user ? `/profile/${user.uid}` : "/"} style={{ ...s.brand, gap: 8 }} onClick={() => setMobileOpen(false)}>
            <img src="/images/logoRA.png" alt="RestaurAdmin" style={{ height: 32, width: "auto" }} />
            <span style={{ ...s.brandText, fontSize: 16 }}>{t("navbar.brand")}</span>
          </Link>
          <button style={s.drawerClose} onClick={() => setMobileOpen(false)} aria-label="Close menu">
            <BsX size={22} />
          </button>
        </div>

        <div style={s.drawerBody}>
          {navItems.map((item) => (
            <Link
              key={item.check}
              to={item.to}
              style={s.drawerLink(isActive(item.check))}
              onClick={() => setMobileOpen(false)}
            >
              {item.icon}
              {item.label}
            </Link>
          ))}
        </div>

        <div style={s.drawerFooter}>
          <div style={{ display: "flex", justifyContent: "center", paddingBottom: 4 }}>
            <LanguageSwitcher />
          </div>
          {user ? (
            <button
              style={{
                ...s.mobileAuthBtn,
                background: "rgba(255,255,255,0.06)",
                color: "rgba(255,255,255,0.8)",
              }}
              onClick={() => { setMobileOpen(false); handleSignOut(); }}
            >
              <BsBoxArrowInLeft size={16} />
              {t("navbar.signOut")}
            </button>
          ) : (
            <button
              style={{
                ...s.mobileAuthBtn,
                background: C.gold,
                color: C.bg,
              }}
              onClick={() => { setMobileOpen(false); setShowModal(true); }}
            >
              <BsBoxArrowInRight size={16} />
              {t("navbar.signIn")}
            </button>
          )}
        </div>
      </div>

      {/* ── Auth Modal ───────────────────────────────────── */}
      {!user && (
        <Modal
          show={showModal}
          onHide={closeModal}
          centered
          className="auth-modal"
          backdrop="static"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <BsBoxArrowInRight className="modal-title-icon" />
              {t("navbar.signIn")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="modal-logo">
              <img src="/images/logoRA.png" alt="RestaurAdmin" />
            </div>

            {error && (
              <div className="auth-error">
                <BsExclamationTriangle className="auth-error-icon" />
                {error}
              </div>
            )}

            <Form>
              <Form.Group className="mb-4">
                <Form.Label>
                  <BsEnvelope className="form-label-icon" />
                  {t("navbar.auth.emailLabel")}
                </Form.Label>
                <div className="position-relative">
                  <BsEnvelope className="input-icon" />
                  <Form.Control
                    type="email"
                    placeholder={t("navbar.auth.emailPlaceholder")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyPress={handleKeyPress}
                    autoComplete="email"
                  />
                </div>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>
                  <BsLock className="form-label-icon" />
                  {t("navbar.auth.passwordLabel")}
                </Form.Label>
                <div className="position-relative">
                  <BsLock className="input-icon" />
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    placeholder={t("navbar.auth.passwordPlaceholder")}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyPress={handleKeyPress}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? t("navbar.auth.hidePassword") : t("navbar.auth.showPassword")}
                  >
                    {showPassword ? <BsEyeSlash /> : <BsEye />}
                  </button>
                </div>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>
              {t("navbar.auth.cancel")}
            </Button>
            <Button variant="primary" onClick={handleSignIn} disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  {t("navbar.auth.signingIn")}
                </>
              ) : (
                <>
                  {t("navbar.signIn")}
                  <BsArrowRightShort className="btn-icon" />
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* ── Responsive CSS ───────────────────────────────── */}
      <style>{`
        .ra-desktop-only { display: flex !important; }
        .ra-mobile-only  { display: none !important; }
        @media (max-width: 768px) {
          .ra-desktop-only { display: none !important; }
          .ra-mobile-only  { display: flex !important; }
        }
      `}</style>
    </>
  );
};

// ─── Desktop nav sub-component ──────────────────────────────────
function DesktopNav({ navItems, user, hovered, setHovered, isActive: checkActive, handleSignOut, openSignIn, s, t }) {
  return (
    <div style={s.desktopNav} className="ra-desktop-only">
      {navItems.map((item) => {
        const active = checkActive(item.check);
        const hover = hovered === item.check;
        return (
          <Link
            key={item.check}
            to={item.to}
            style={{ ...s.navLink(active, hover), position: "relative" }}
            onMouseEnter={() => setHovered(item.check)}
            onMouseLeave={() => setHovered(null)}
          >
            {item.icon}
            {item.label}
            {active && <span style={s.activeIndicator} />}
          </Link>
        );
      })}

      <div style={s.divider} />

      <LanguageSwitcher />

      {user ? (
        <button
          style={s.authBtn(hovered === "signout")}
          onMouseEnter={() => setHovered("signout")}
          onMouseLeave={() => setHovered(null)}
          onClick={handleSignOut}
        >
          <BsBoxArrowInLeft size={15} />
          {t("navbar.signOut")}
        </button>
      ) : (
        <button
          style={s.authBtn(hovered === "signin")}
          onMouseEnter={() => setHovered("signin")}
          onMouseLeave={() => setHovered(null)}
          onClick={openSignIn}
        >
          <BsBoxArrowInRight size={15} />
          {t("navbar.signIn")}
        </button>
      )}
    </div>
  );
}

export default ResponsiveAppBar;
